import { inject, Injectable } from '@angular/core';
import { HEADERS } from '../api-headers';
import { HttpClient } from '@angular/common/http';

import { toFormBody } from '../api-utils';

import {
  DASHBOARD_PUT,
  DASHBOARD_POST_COPY_DASHBOARD,
  DASHBOARD_POST_CREATE_WIDGET_PAGE,
  DASHBOARD_DELETE,
  DASHBOARD_POST_DELETE_WIDGET_PAGE,
  DASHBOARD_POST_EDIT_WIDGET_PAGE,
  DASHBOARD_GET_WIDGET_PAGES,
  DASHBOARD_POST_REORDER_WIDGET_PAGES,
  DASHBOARD_PATCH_PATCH,
  DASHBOARD_PATCH_CONTEXT_MENU_OPTIONS,
} from '../endpoints/dashboard.endpoints';

import { Dashboard, DashboardPage } from 'src/app/core/models/core.models';
import { WidgetViewProperties } from './feed-api.service';
import {
  CreateDashboardPageParams,
  ReorderWidgetPageParams,
  UpdateDashboardPageParams,
} from 'src/app/core/models/widget.models';

import { Observable } from 'rxjs';

export interface CopyDashboardParams {
  workspace_id: string;

  // Single or multi dashboard copy
  object_ids: string[];

  // Used when copying one dashboard
  title?: string;
  description?: string;

  // Used when copying multiple dashboards
  prefix?: string;

  duplicateExportSchedules?: boolean;
  duplicatePermissions?: boolean;
}

export interface CopyDashboardResponse {
  dashboards: Dashboard[];
}

@Injectable({
  providedIn: 'root',
})
export class DashboardApiService {
  private readonly http = inject(HttpClient);

  copyDashboard(
    params: CopyDashboardParams,
  ): Observable<CopyDashboardResponse> {
    return this.http.post<CopyDashboardResponse>(
      DASHBOARD_POST_COPY_DASHBOARD,
      params,
    );
  }

  getDashboardPages(dashboardId: string | number): Observable<DashboardPage[]> {
    return this.http.get<DashboardPage[]>(
      DASHBOARD_GET_WIDGET_PAGES(dashboardId.toString()),
    );
  }

  updateDashboard(
    id: string,
    dashboard: Record<string, unknown>,
  ): Observable<Dashboard> {
    return this.http.patch<Dashboard>(
      DASHBOARD_PATCH_PATCH(id),
      toFormBody(dashboard),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  saveDashboardSettings(
    dashboardId: string,
    viewProperties: WidgetViewProperties,
  ): Observable<unknown> {
    return this.http.patch(
      DASHBOARD_PATCH_CONTEXT_MENU_OPTIONS(dashboardId),
      toFormBody({ view_properties: viewProperties }),
      { headers: HEADERS.formUrlEncoded },
    );
  }

  createDashboardPage({
    dashboardId,
    name,
    description,
    durationSeconds,
    period,
  }: CreateDashboardPageParams): Observable<DashboardPage> {
    return this.http.post<DashboardPage>(
      DASHBOARD_POST_CREATE_WIDGET_PAGE(dashboardId.toString()),
      {
        name,
        description,
        duration_seconds: durationSeconds,
        period,
      },
    );
  }

  updateDashboardPage({
    dashboard_id,
    page_id,
    name,
    description,
    duration_seconds,
    period,
    is_visible_in_presentation,
  }: UpdateDashboardPageParams): Observable<DashboardPage> {
    return this.http.post<DashboardPage>(
      DASHBOARD_POST_EDIT_WIDGET_PAGE(dashboard_id.toString()),
      {
        page_id,
        name,
        description,
        duration_seconds,
        period,
        is_visible_in_presentation,
      },
    );
  }

  reorderDashboardPages({
    dashboardId,
    orderedPageIds,
  }: ReorderWidgetPageParams): Observable<{ success: boolean }> {
    return this.http.post<{
      success: boolean;
    }>(DASHBOARD_POST_REORDER_WIDGET_PAGES(dashboardId.toString()), {
      ordered_page_ids: orderedPageIds,
    });
  }

  deleteDashboardPage(
    dashboardId: string | number,
    pageId: number,
  ): Observable<DashboardPage[]> {
    return this.http.post<DashboardPage[]>(
      DASHBOARD_POST_DELETE_WIDGET_PAGE(dashboardId.toString()),
      {
        page_id: pageId,
      },
    );
  }

  deleteDashboard(dashboardId: string): Observable<any> {
    return this.http.delete(DASHBOARD_DELETE(dashboardId));
  }

  createDashboard(dashboard: Record<string, unknown>): Observable<Dashboard> {
    return this.http.put<Dashboard>(DASHBOARD_PUT, toFormBody(dashboard), {
      headers: HEADERS.formUrlEncoded,
    });
  }
}
